import { Suspense, useEffect, useMemo, useRef, useState } from "react";
import { Canvas, useFrame } from "@react-three/fiber";
import { OrbitControls, ContactShadows, useFBX, useGLTF, PerspectiveCamera } from "@react-three/drei";
import * as THREE from "three";
import { Car, Lightbulb, Volume2, Armchair, Palette, Save, Download, Loader2, Library, FolderOpen, RotateCw, Eye, EyeOff, Plus, X } from "lucide-react";
import { toast } from "sonner";
import api, { formatApiErrorDetail } from "@/lib/api";
import { startSiren, stopSiren, loadCustomSiren, startBeeper, stopBeeper } from "./siren";
import { normalizeFbx } from "./modelUtils";
import { buildFullBar, buildMiniBar, buildBeacon, buildDashLight, buildPerimeterHead, buildPushBar, applyFlash } from "./lightbarModels";

const buildLightbarModel = (kind, modules) => ({
  fullbar: buildFullBar, minibar: buildMiniBar, beacon: buildBeacon, dashlight: buildDashLight, perimeter: buildPerimeterHead, pushbar: buildPushBar,
}[kind] || buildFullBar)(modules);

const ASSETS = "/vehicles";

// anchors are fractions of the normalized car bounding box: [x/width, y/height, z/length]
const EQUIPMENT = [
  { id: "lightbar", label: "Roof Lightbar — Liberty Full Bar", cat: "Lighting", anchor: [0, 0.985, -0.06], kind: "fullbar", scaleTo: 1.2 },
  { id: "minibar", label: "Roof — Micro Mini Bar", cat: "Lighting", anchor: [0, 0.99, -0.06], kind: "minibar", scaleTo: 1.15 },
  { id: "beacon", label: "Beacon — Rear Deck", cat: "Lighting", anchor: [0, 0.72, -0.42], kind: "beacon", scaleTo: 0.5 },
  { id: "visor_front", label: "Dash Light — Front Visor", cat: "Lighting", anchor: [0, 0.78, 0.22], kind: "dashlight", scaleTo: 1.25 },
  { id: "visor_rear", label: "Deck Light — Rear", cat: "Lighting", anchor: [0, 0.78, -0.30], kind: "dashlight", scaleTo: 1.25 },
  { id: "alley_l", label: "Alley Light — Left", cat: "Lighting", anchor: [-0.40, 1.04, -0.06], kind: "beam", dir: [-1, -0.25, 0.15] },
  { id: "alley_r", label: "Alley Light — Right", cat: "Lighting", anchor: [0.40, 1.04, -0.06], kind: "beam", dir: [1, -0.25, 0.15] },
  { id: "spotlight", label: "A-Pillar Spotlight", cat: "Lighting", anchor: [-0.46, 0.62, 0.22], kind: "beam", dir: [-1, -0.05, 0.75] },
  { id: "grille", label: "Perimeter Heads — Grille", cat: "Lighting", anchor: [0, 0.30, 0.505], kind: "perimeter", scaleTo: 0.55 },
  { id: "rear_dir", label: "Perimeter Heads — Rear", cat: "Lighting", anchor: [0, 0.45, -0.505], kind: "perimeter", scaleTo: 0.55 },
  { id: "pushbar", label: "Bumper Push Bar", cat: "Exterior", anchor: [0, 0.05, 0.53], kind: "pushbar", scaleTo: 1.0 },
  { id: "antenna_roof", label: "Antenna — Roof", cat: "Exterior", anchor: [0.14, 1.02, -0.22], kind: "antenna" },
  { id: "antenna_trunk", label: "Antenna — Trunk", cat: "Exterior", anchor: [-0.16, 0.72, -0.46], kind: "antenna" },
  { id: "laptop", label: "Police Laptop / MDT", cat: "Interior", anchor: [0.10, 0.50, 0.06], kind: "glb", src: `${ASSETS}/police_computer.glb`, targetWidth: 0.34 },
  { id: "radar", label: "Radar Unit", cat: "Interior", anchor: [0.16, 0.60, 0.26], kind: "prop_radar" },
  { id: "rifle", label: "Rifle Holder", cat: "Interior", anchor: [0.30, 0.42, -0.10], kind: "prop_rifle" },
  { id: "coffee", label: "Coffee Cup", cat: "Interior", anchor: [-0.10, 0.44, 0.02], kind: "prop_coffee" },
  { id: "notepad", label: "Duty Notepad", cat: "Interior", anchor: [0.06, 0.56, 0.24], kind: "prop_notepad" },
  { id: "radio2", label: "Console Radio", cat: "Interior", anchor: [-0.02, 0.46, 0.10], kind: "prop_radio" },
];

const LIGHT_MODULES = {
  fullbar: [{ color: "red" }, { color: "red" }, { color: "white" }, { color: "blue" }, { color: "blue" }],
  minibar: [{ color: "red" }, { color: "blue" }, { color: "amber" }],
  beacon: [{ color: "red" }, { color: "blue" }],
  dashlight: [{ color: "red" }, { color: "blue" }],
  perimeter: [{ color: "amber" }, { color: "red" }],
};

const RIM_STYLES = ["stock", "steelie", "sport", "pursuit"];
const norm = (v) => Math.max(Math.abs(v.x), Math.abs(v.y), Math.abs(v.z));

/* ---------- 3D primitives ---------- */

function Beam({ position, dir }) {
  const v = useMemo(() => new THREE.Vector3(...dir).normalize(), [dir]);
  const quat = useMemo(() => new THREE.Quaternion().setFromUnitVectors(new THREE.Vector3(0, -1, 0), v), [v]);
  return (
    <group position={position} quaternion={quat}>
      <mesh position={[0, -1.6, 0]}>
        <coneGeometry args={[0.85, 3.2, 18, 1, true]} />
        <meshBasicMaterial color="#fff7d6" transparent opacity={0.10} side={THREE.DoubleSide} depthWrite={false} />
      </mesh>
      <mesh><cylinderGeometry args={[0.07, 0.09, 0.16, 12]} /><meshStandardMaterial color="#17171c" roughness={0.4} metalness={0.6} /></mesh>
      <spotLight position={[0, 0, 0]} target-position={[v.x * 8, v.y * 8, v.z * 8]} angle={0.35} penumbra={0.6} intensity={26} color="#fff3c4" distance={22} />
    </group>
  );
}

function Antenna({ position }) {
  return (
    <group position={position}>
      <mesh position={[0, 0.02, 0]}><cylinderGeometry args={[0.05, 0.06, 0.05, 10]} /><meshStandardMaterial color="#0c0c10" roughness={0.5} /></mesh>
      <mesh position={[0, 0.24, 0]}><cylinderGeometry args={[0.008, 0.014, 0.44, 6]} /><meshStandardMaterial color="#1c1c22" roughness={0.35} metalness={0.7} /></mesh>
    </group>
  );
}

function Prop({ kind, position }) {
  const dark = "#15151a";
  const shapes = {
    prop_radar: <group><mesh><boxGeometry args={[0.14, 0.09, 0.1]} /><meshStandardMaterial color={dark} /></mesh><mesh position={[0, 0.02, 0.06]} rotation={[0.5, 0, 0]}><boxGeometry args={[0.1, 0.06, 0.01]} /><meshStandardMaterial color="#0a2a12" emissive="#22c55e" emissiveIntensity={1.4} /></mesh></group>,
    prop_rifle: <group rotation={[0, 0.2, 0]}><mesh><boxGeometry args={[0.05, 0.5, 0.08]} /><meshStandardMaterial color="#1c1c22" /></mesh><mesh position={[0, 0.05, 0.06]}><boxGeometry args={[0.035, 0.34, 0.035]} /><meshStandardMaterial color="#0a0a0c" metalness={0.7} roughness={0.3} /></mesh></group>,
    prop_coffee: <group><mesh><cylinderGeometry args={[0.035, 0.028, 0.09, 14]} /><meshStandardMaterial color="#f5f0e6" /></mesh><mesh position={[0, 0.048, 0]}><cylinderGeometry args={[0.037, 0.037, 0.012, 14]} /><meshStandardMaterial color="#c22727" /></mesh></group>,
    prop_notepad: <mesh rotation={[-0.4, 0.3, 0]}><boxGeometry args={[0.16, 0.012, 0.22]} /><meshStandardMaterial color="#e8e2d0" /></mesh>,
    prop_radio: <group><mesh><boxGeometry args={[0.18, 0.06, 0.12]} /><meshStandardMaterial color={dark} /></mesh><mesh position={[0.04, 0.035, 0.03]}><boxGeometry args={[0.06, 0.02, 0.04]} /><meshStandardMaterial color="#0a1a2a" emissive="#2e6bff" emissiveIntensity={1.2} /></mesh></group>,
  };
  return <group position={position}>{shapes[kind]}</group>;
}

function StrobingModel({ kind, scaleTo, position }) {
  const modules = LIGHT_MODULES[kind] || [];
  const group = useMemo(() => {
    const g = buildLightbarModel(kind, modules);
    if (kind === "fullbar" || kind === "minibar" || kind === "beacon" || kind === "dashlight" || kind === "perimeter") {
      const box = new THREE.Box3().setFromObject(g);
      const size = box.getSize(new THREE.Vector3());
      const s = scaleTo / Math.max(size.x, size.z);
      g.scale.setScalar(s);
      // recentre so the group's origin sits at its base
      const box2 = new THREE.Box3().setFromObject(g);
      const ctr = box2.getCenter(new THREE.Vector3());
      g.position.set(-ctr.x, -box2.min.y, -ctr.z);
      const wrap = new THREE.Group();
      wrap.add(g);
      return wrap;
    }
    return g;
  }, [kind, scaleTo]);
  useFrame(({ clock }) => {
    const inner = group.children[0] || group;
    const target = group.userData?.lenses ? group : inner;
    const phase = Math.floor(clock.elapsedTime * 7) % 2;
    const active = modules.map((_, i) => (i % 2 === phase ? 1 : 0));
    applyFlash(target, active, modules);
  });
  return <primitive object={group} position={position} />;
}

function GlbEquip({ item, position }) {
  const { scene } = useGLTF(item.src);
  const model = useMemo(() => {
    const c = scene.clone(true);
    const box = new THREE.Box3().setFromObject(c);
    const size = box.getSize(new THREE.Vector3());
    const s = item.targetWidth / norm(size);
    c.scale.setScalar(s);
    const box2 = new THREE.Box3().setFromObject(c);
    const ctr = box2.getCenter(new THREE.Vector3());
    c.position.sub(ctr);
    return c;
  }, [scene, item.targetWidth]);
  return <primitive object={model} position={position} />;
}

/* ---------- the car ---------- */

function CarModel({ paint, stealth, tint, rims, onReady }) {
  const fbx = useFBX(`${ASSETS}/lynx_sm.fbx`);
  const [wheelPos, setWheelPos] = useState([]);
  const model = useMemo(() => {
    const c = normalizeFbx(fbx, 4.8);
    c.userData.finalBox = c.userData.box;
    return c;
  }, [fbx]);

  useEffect(() => {
    const wheels = [];
    model.traverse((o) => {
      if (o.isMesh && /wheel|tire|tyre|rim/i.test(o.name || "")) {
        const p = new THREE.Vector3();
        new THREE.Box3().setFromObject(o).getCenter(p);
        wheels.push(p);
      }
    });
    setWheelPos(wheels);
    onReady?.(model.userData.finalBox);
  }, [model, onReady]);

  useEffect(() => {
    model.traverse((o) => {
      if (!o.isMesh) return;
      const mats = Array.isArray(o.material) ? o.material : [o.material];
      mats.forEach((m) => {
        if (!m) return;
        if (!m.userData.orig) m.userData.orig = { color: m.color?.clone(), opacity: m.opacity, transparent: m.transparent, roughness: m.roughness };
        const name = (m.name || o.name || "").toLowerCase();
        if (/glass|window|windshield|windscreen/.test(name)) {
          m.transparent = true;
          m.opacity = Math.max(0.12, 0.9 - tint * 0.8);
          m.color?.set(new THREE.Color(0.06 + 0.2 * (1 - tint), 0.08 + 0.2 * (1 - tint), 0.1 + 0.22 * (1 - tint)));
        } else if (!/tire|tyre|rubber|wheel|chrome|light|lens|interior|seat|dash|engine/.test(name)) {
          if (stealth) { m.color?.set("#0c0c0e"); if ("roughness" in m) m.roughness = 0.92; if ("metalness" in m) m.metalness = 0.15; }
          else { m.color?.set(paint); if ("roughness" in m) m.roughness = 0.35; if ("metalness" in m) m.metalness = 0.4; }
        }
        m.needsUpdate = true;
      });
    });
  }, [model, paint, stealth, tint]);

  return (
    <group>
      <primitive object={model} />
      {rims !== "stock" && wheelPos.map((p, i) => (
        <group key={i} position={p} rotation={[0, 0, Math.PI / 2]}>
          <mesh>
            <cylinderGeometry args={[0.16, 0.16, 0.06, 18]} />
            <meshStandardMaterial color={rims === "steelie" ? "#3a3a40" : rims === "sport" ? "#c0c0c8" : "#1a1a20"} metalness={rims === "sport" ? 0.9 : 0.5} roughness={0.3} />
          </mesh>
          {rims === "pursuit" && <mesh><torusGeometry args={[0.13, 0.02, 8, 20]} /><meshStandardMaterial color="#FF6B1A" metalness={0.8} roughness={0.25} /></mesh>}
        </group>
      ))}
    </group>
  );
}

/* ---------- panel bits ---------- */

const inputCls = "w-full bg-[#050505] border border-white/10 px-2.5 py-2 font-mono text-[11px] text-zinc-100 rounded-none focus:outline-none focus:ring-1 focus:ring-nc-blue";
const labelCls = "font-mono text-[8px] tracking-[0.25em] text-zinc-500 uppercase block mb-1";

function EquipRow({ item, enabled, selected, onToggle, onSelect }) {
  return (
    <div className={`flex items-center gap-2 border px-2.5 py-2 transition-colors ${selected ? "border-nc-orange bg-nc-orange/5" : "border-white/10 hover:border-white/25"}`}>
      <button data-testid={`equip-toggle-${item.id}`} onClick={() => onToggle(item.id)} className={`w-4 h-4 border flex items-center justify-center transition-colors ${enabled ? "bg-nc-blue border-nc-blue" : "border-white/25"}`}>
        {enabled && <Plus className="w-3 h-3 text-white rotate-45" />}
      </button>
      <button onClick={() => onSelect(item.id)} className="flex-1 text-left font-mono text-[10px] text-zinc-200 truncate">{item.label}</button>
      {enabled && <button data-testid={`equip-nudge-${item.id}`} onClick={() => onSelect(item.id)} className={`font-mono text-[7px] uppercase border px-1.5 py-0.5 ${selected ? "border-nc-orange text-nc-orange" : "border-white/15 text-zinc-500"}`}>nudge</button>}
    </div>
  );
}

export default function VehicleStudio({ me }) {
  const [paint, setPaint] = useState("#1a2a4a");
  const [stealth, setStealth] = useState(false);
  const [tint, setTint] = useState(0.35);
  const [rims, setRims] = useState("stock");
  const [enabled, setEnabled] = useState(["lightbar", "pushbar"]);
  const [offsets, setOffsets] = useState({});
  const [selected, setSelected] = useState(null);
  const [camPreset, setCamPreset] = useState("orbit");
  const [siren, setSiren] = useState({ type: "wail", rate: 1, playing: false, customName: "" });
  const [beeper, setBeeper] = useState(false);
  const [saveName, setSaveName] = useState("Lynx Patrol Build");
  const [busy, setBusy] = useState("");
  const [lib, setLib] = useState(null);
  const [box, setBox] = useState(null);
  const controlsRef = useRef(null);

  useEffect(() => () => { stopSiren(); stopBeeper(); }, []);

  const pos = (item) => {
    if (!box) return [0, 0, 0];
    const size = box.getSize(new THREE.Vector3());
    const o = offsets[item.id] || [0, 0, 0];
    return [item.anchor[0] * size.x + o[0], item.anchor[1] * size.y + o[1], item.anchor[2] * size.z + o[2]];
  };

  const toggle = (id) => setEnabled((e) => (e.includes(id) ? e.filter((x) => x !== id) : [...e, id]));

  const toggleSiren = async () => {
    if (siren.playing) { stopSiren(); setSiren((s) => ({ ...s, playing: false })); }
    else { startSiren(siren.type, siren.rate); setSiren((s) => ({ ...s, playing: true })); }
  };
  const toggleBeeper = () => { if (beeper) stopBeeper(); else startBeeper(); setBeeper(!beeper); };

  const config = () => ({
    paint, stealth, tint, rims, beeper, siren: { type: siren.type, rate: siren.rate, customName: siren.customName },
    equipment: enabled.map((id) => ({ id, offset: offsets[id] || [0, 0, 0] })),
  });

  const loadConfig = (cfg) => {
    setPaint(cfg.paint || "#1a2a4a"); setStealth(!!cfg.stealth); setTint(cfg.tint ?? 0.35); setRims(cfg.rims || "stock");
    setEnabled((cfg.equipment || []).map((e) => e.id));
    setOffsets(Object.fromEntries((cfg.equipment || []).map((e) => [e.id, e.offset || [0, 0, 0]])));
    setSiren((s) => ({ ...s, type: cfg.siren?.type || "wail", rate: cfg.siren?.rate || 1, customName: cfg.siren?.customName || "" }));
  };

  const save = async (isPublic) => {
    if (!me) { toast.error("Sign in to save builds"); return; }
    if (saveName.trim().length < 3) { toast.error("Name your build (3+ chars)"); return; }
    setBusy("save");
    try {
      await api.post("/ncmod/vehicle", { name: saveName.trim(), description: stealth ? "Stealth spec" : "Patrol spec", config: config(), public: isPublic });
      toast.success(isPublic ? "Shared to the Community Library" : "Build saved");
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); } finally { setBusy(""); }
  };

  const exportManifest = () => {
    const manifest = {
      vehicle_id: "veh_lynx_patrol", engine_target: "UE5.1", build_name: saveName,
      paint: stealth ? "#0c0c0e" : paint, finish: stealth ? "matte_stealth" : "gloss", window_tint: tint, rims,
      reverse_beeper: beeper, siren: { type: siren.type, rate: siren.rate, custom_sample: siren.customName || null },
      equipment: enabled.map((id) => { const it = EQUIPMENT.find((x) => x.id === id); return { id, label: it.label, category: it.cat, position: pos(it).map((n) => +n.toFixed(3)), mesh: it.src ? it.src.split("/").pop() : "procedural" }; }),
    };
    const blob = new Blob([JSON.stringify(manifest, null, 2)], { type: "application/json" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob);
    a.download = `${saveName.replace(/\W+/g, "_")}_build.json`;
    a.click();
    toast.success("Build manifest exported — drops into NewCitizen/Content/Mods/Vehicles/");
  };

  const openLibrary = async () => {
    if (lib) { setLib(null); return; }
    try {
      const r = await api.get("/ncmod/vehicle/library");
      setLib(r.data.items || []);
    } catch { toast.error("Could not load library"); }
  };

  const CAM_PRESETS = {
    orbit: { pos: [5.4, 2.8, 6.4], tgt: [0, 0.9, 0], fov: 42, min: 3, max: 18, pan: false },
    front: { pos: [3.4, 1.4, 5.2], tgt: [0, 0.9, 0], fov: 42, min: 2, max: 12, pan: false },
    interior: { pos: [0.55, 1.05, 0.5], tgt: [0, 0.75, -0.6], fov: 68, min: 0.2, max: 3, pan: true },
    driver: { pos: [0.42, 1.0, 0.1], tgt: [0.42, 0.92, 5], fov: 72, min: 0.05, max: 1.1, pan: false },
    passenger: { pos: [-0.42, 1.0, 0.1], tgt: [-0.42, 0.92, 5], fov: 72, min: 0.05, max: 1.1, pan: false },
  };
  const camTarget = CAM_PRESETS[camPreset] || CAM_PRESETS.orbit;

  const selItem = EQUIPMENT.find((e) => e.id === selected);
  const cats = [...new Set(EQUIPMENT.map((e) => e.cat))];

  return (
    <div data-testid="vehicle-studio" className="grid grid-cols-1 xl:grid-cols-[1fr_340px] gap-4">
      {/* viewport */}
      <div className="flex flex-col gap-3">
        <div data-testid="vehicle-viewport" className="relative border border-white/10 bg-[#08080a] h-[560px] overflow-hidden">
          <Canvas camera={{ position: [5.4, 2.8, 6.4], fov: 42 }}>
            <color attach="background" args={["#08080a"]} />
            <fog attach="fog" args={["#08080a", 20, 55]} />
            <ambientLight intensity={0.5} />
            <directionalLight position={[6, 10, 4]} intensity={1.2} />
            <spotLight position={[-8, 9, -6]} intensity={50} angle={0.7} penumbra={0.8} color="#2E6BFF" />
            <pointLight position={[0, 1.15, 0]} intensity={2.2} distance={3.5} color="#fff4e0" />
            <Suspense fallback={null}>
              <CarModel paint={paint} stealth={stealth} tint={tint} rims={rims} onReady={setBox} />
              {box && EQUIPMENT.filter((e) => enabled.includes(e.id)).map((item) => {
                const p = pos(item);
                if (["fullbar", "minibar", "beacon", "dashlight", "perimeter", "pushbar"].includes(item.kind)) return <StrobingModel key={item.id} kind={item.kind} scaleTo={item.scaleTo || 1} position={p} />;
                if (item.kind === "glb") return <GlbEquip key={item.id} item={item} position={p} />;
                if (item.kind === "beam") return <Beam key={item.id} position={p} dir={item.dir} />;
                if (item.kind === "antenna") return <Antenna key={item.id} position={p} />;
                return <Prop key={item.id} kind={item.kind} position={p} />;
              })}
            </Suspense>
            <ContactShadows position={[0, -0.02, 0]} opacity={0.7} scale={16} blur={2.2} />
            <gridHelper args={[40, 40, "#17171d", "#0e0e12"]} position={[0, -0.03, 0]} />
            <PerspectiveCamera key={`cam-${camPreset}`} makeDefault position={camTarget.pos} fov={camTarget.fov} />
            <OrbitControls key={`ctl-${camPreset}`} ref={controlsRef} makeDefault target={camTarget.tgt} enablePan={camTarget.pan} minDistance={camTarget.min} maxDistance={camTarget.max} maxPolarAngle={Math.PI / 1.9} />
          </Canvas>
          <div className="absolute top-3 left-3 bg-black/70 backdrop-blur border border-white/10 px-3 py-2">
            <p className="font-mono text-[10px] text-zinc-100">Lynx Interceptor — Pursuit Spec</p>
            <p className="font-mono text-[8px] text-zinc-500">{enabled.length} equipment fitted · UE5.1 target</p>
          </div>
          <div className="absolute bottom-3 left-1/2 -translate-x-1/2 flex items-center gap-1 bg-black/80 backdrop-blur border border-white/10 px-1.5 py-1.5">
            {[["orbit", RotateCw, "Exterior"], ["front", Eye, "Front"], ["interior", Armchair, "Cabin"], ["driver", Car, "Driver Seat"], ["passenger", Armchair, "Passenger"]].map(([m, Icon, label]) => (
              <button key={m} data-testid={`vehicle-cam-${m}`} onClick={() => setCamPreset(m)} className={`font-mono text-[8px] uppercase tracking-[0.15em] px-3 py-1.5 transition-colors flex items-center gap-1.5 ${camPreset === m ? "bg-nc-blue text-white" : "text-zinc-500 hover:text-zinc-200"}`}>
                <Icon className="w-3 h-3" /> {label}
              </button>
            ))}
          </div>
        </div>

        {/* save/share/export */}
        <div className="border border-white/10 bg-[#070707] p-4 flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-44"><span className={labelCls}>Build name</span><input data-testid="vehicle-name" className={inputCls} value={saveName} maxLength={60} onChange={(e) => setSaveName(e.target.value)} /></div>
          <button data-testid="vehicle-save" disabled={busy === "save"} onClick={() => save(false)} className="font-mono text-[9px] tracking-[0.15em] uppercase bg-nc-blue hover:bg-nc-blue/80 text-white px-4 py-2.5 transition-colors flex items-center gap-1.5 disabled:opacity-50">
            {busy === "save" ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />} Save
          </button>
          <button data-testid="vehicle-share" disabled={busy === "save"} onClick={() => save(true)} className="font-mono text-[9px] tracking-[0.15em] uppercase border border-nc-orange/50 text-nc-orange hover:bg-nc-orange/10 px-3 py-2.5 transition-colors">Share</button>
          <button data-testid="vehicle-library" onClick={openLibrary} className="font-mono text-[9px] tracking-[0.15em] uppercase border border-white/15 text-zinc-400 hover:text-zinc-100 px-3 py-2.5 transition-colors flex items-center gap-1.5"><Library className="w-3.5 h-3.5" /> Builds</button>
          <button data-testid="vehicle-export" onClick={exportManifest} className="font-mono text-[9px] tracking-[0.15em] uppercase border border-nc-blue/50 text-nc-blue hover:bg-nc-blue/10 px-3 py-2.5 transition-colors flex items-center gap-1.5"><Download className="w-3.5 h-3.5" /> Build JSON</button>
        </div>
        {lib && (
          <div data-testid="vehicle-lib" className="border border-white/10 bg-[#070707] p-3 flex flex-col gap-1.5 max-h-44 overflow-y-auto">
            {lib.length === 0 && <p className="font-mono text-[10px] text-zinc-600 p-2">No shared builds yet.</p>}
            {lib.map((b, i) => (
              <button key={b.id} data-testid={`vehicle-lib-${i}`} onClick={() => { loadConfig(b.config); setSaveName(b.name + " (remix)"); setLib(null); toast.success(`Loaded "${b.name}"`); }} className="flex items-center justify-between text-left border border-white/5 hover:border-nc-blue/40 px-3 py-2 transition-colors">
                <span className="font-mono text-[10px] text-zinc-200">{b.name}</span>
                <span className="font-mono text-[8px] text-zinc-500 uppercase">by {b.author} · {(b.config?.equipment || []).length} items</span>
              </button>
            ))}
          </div>
        )}
      </div>

      {/* config panel */}
      <div className="flex flex-col gap-4 max-h-[640px] overflow-y-auto pr-1">
        <div className="border border-white/10 bg-[#070707] p-4 flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.3em] text-nc-blue uppercase flex items-center gap-2"><Palette className="w-3.5 h-3.5" /> Paint & Finish</p>
          <div className="flex items-center gap-3">
            <input data-testid="vehicle-paint" type="color" value={paint} onChange={(e) => setPaint(e.target.value)} className="w-12 h-9 bg-transparent border border-white/15 cursor-pointer" />
            <button data-testid="vehicle-stealth" onClick={() => setStealth((s) => !s)} className={`flex-1 font-mono text-[9px] uppercase tracking-[0.15em] border px-3 py-2.5 transition-colors ${stealth ? "border-nc-orange text-nc-orange bg-nc-orange/10" : "border-white/15 text-zinc-400"}`}>
              {stealth ? "Stealth spec ON — matte black" : "Make stealth version"}
            </button>
          </div>
          <div>
            <div className="flex justify-between mb-1"><span className={labelCls}>Window tint</span><span className="font-mono text-[10px] text-nc-orange">{Math.round(tint * 100)}%</span></div>
            <input data-testid="vehicle-tint" type="range" min={0} max={1} step={0.05} value={tint} onChange={(e) => setTint(parseFloat(e.target.value))} className="w-full accent-[#FF6B1A]" />
          </div>
          <div>
            <span className={labelCls}>Rims</span>
            <div className="grid grid-cols-4 gap-1.5">
              {RIM_STYLES.map((r) => (
                <button key={r} data-testid={`vehicle-rims-${r}`} onClick={() => setRims(r)} className={`font-mono text-[8px] uppercase border px-1 py-2 transition-colors ${rims === r ? "border-nc-blue text-nc-blue bg-nc-blue/10" : "border-white/10 text-zinc-500"}`}>{r}</button>
              ))}
            </div>
          </div>
        </div>

        {cats.map((cat) => (
          <div key={cat} className="border border-white/10 bg-[#070707] p-4 flex flex-col gap-2">
            <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-400 uppercase flex items-center gap-2">
              {cat === "Lighting" ? <Lightbulb className="w-3.5 h-3.5 text-nc-orange" /> : cat === "Interior" ? <Armchair className="w-3.5 h-3.5 text-nc-orange" /> : <Car className="w-3.5 h-3.5 text-nc-orange" />} {cat} equipment
            </p>
            {EQUIPMENT.filter((e) => e.cat === cat).map((item) => (
              <EquipRow key={item.id} item={item} enabled={enabled.includes(item.id)} selected={selected === item.id} onToggle={toggle} onSelect={setSelected} />
            ))}
          </div>
        ))}

        {selItem && enabled.includes(selItem.id) && (
          <div data-testid="vehicle-nudge-panel" className="border border-nc-orange/40 bg-nc-orange/5 p-4 flex flex-col gap-2.5">
            <div className="flex items-center justify-between">
              <p className="font-mono text-[9px] tracking-[0.25em] text-nc-orange uppercase">Nudge // {selItem.label}</p>
              <button onClick={() => setSelected(null)}><X className="w-3.5 h-3.5 text-zinc-500" /></button>
            </div>
            {["Left/Right", "Up/Down", "Fwd/Back"].map((axis, ai) => (
              <div key={axis}>
                <div className="flex justify-between mb-0.5"><span className={labelCls}>{axis}</span><span className="font-mono text-[9px] text-zinc-400">{((offsets[selItem.id] || [0, 0, 0])[ai]).toFixed(2)}m</span></div>
                <input type="range" min={-1} max={1} step={0.02} value={(offsets[selItem.id] || [0, 0, 0])[ai]} onChange={(e) => { const v = [...(offsets[selItem.id] || [0, 0, 0])]; v[ai] = parseFloat(e.target.value); setOffsets((o) => ({ ...o, [selItem.id]: v })); }} className="w-full accent-[#FF6B1A]" />
              </div>
            ))}
          </div>
        )}

        <div className="border border-white/10 bg-[#070707] p-4 flex flex-col gap-3">
          <p className="font-mono text-[9px] tracking-[0.3em] text-zinc-400 uppercase flex items-center gap-2"><Volume2 className="w-3.5 h-3.5 text-nc-orange" /> Audio</p>
          <div>
            <span className={labelCls}>Siren — synth engine</span>
            <div className="grid grid-cols-4 gap-1.5">
              {["wail", "yelp", "phaser", "hilow"].map((s) => (
                <button key={s} data-testid={`siren-${s}`} onClick={() => { setSiren((x) => ({ ...x, type: s })); if (siren.playing) startSiren(s, siren.rate); }} className={`font-mono text-[8px] uppercase border px-1 py-2 transition-colors ${siren.type === s ? "border-nc-blue text-nc-blue bg-nc-blue/10" : "border-white/10 text-zinc-500"}`}>{s}</button>
              ))}
            </div>
          </div>
          <div>
            <div className="flex justify-between mb-1"><span className={labelCls}>Cycle rate</span><span className="font-mono text-[10px] text-nc-orange">{siren.rate}x</span></div>
            <input data-testid="siren-rate" type="range" min={0.5} max={2} step={0.1} value={siren.rate} onChange={(e) => { const r = parseFloat(e.target.value); setSiren((x) => ({ ...x, rate: r })); if (siren.playing) startSiren(siren.type, r); }} className="w-full accent-[#FF6B1A]" />
          </div>
          <label className="font-mono text-[9px] uppercase border border-dashed border-white/20 text-zinc-400 hover:border-nc-blue/40 px-3 py-2.5 cursor-pointer transition-colors flex items-center gap-2">
            <FolderOpen className="w-3.5 h-3.5" /> {siren.customName ? `Custom: ${siren.customName}` : "Import custom siren audio…"}
            <input data-testid="siren-import" type="file" accept="audio/*" className="hidden" onChange={async (e) => { const f = e.target.files?.[0]; if (!f) return; try { await loadCustomSiren(f); setSiren((x) => ({ ...x, type: "custom", customName: f.name })); toast.success("Custom siren loaded"); } catch { toast.error("Could not decode audio"); } }} />
          </label>
          <div className="flex gap-2">
            <button data-testid="siren-play" onClick={toggleSiren} className={`flex-1 font-mono text-[9px] uppercase tracking-[0.15em] px-3 py-2.5 transition-colors ${siren.playing ? "bg-red-500/80 text-white" : "bg-nc-blue hover:bg-nc-blue/80 text-white"}`}>
              {siren.playing ? "Kill Siren" : "Test Siren"}
            </button>
            <button data-testid="beeper-toggle" onClick={toggleBeeper} className={`flex-1 font-mono text-[9px] uppercase tracking-[0.15em] border px-3 py-2.5 transition-colors ${beeper ? "border-nc-orange text-nc-orange bg-nc-orange/10" : "border-white/15 text-zinc-400"}`}>
              {beeper ? "Beeper ON" : "Reverse Beeper"}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
